// pages/tools/UTMBuilder.jsx
import { useState } from 'react';

export default function UTMBuilder() {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [term, setTerm] = useState('');
  const [content, setContent] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [showCopied, setShowCopied] = useState(false);
  const [urlHistory, setUrlHistory] = useState([]);

  // Quick Templates
  const templates = [
    {
      name: 'Facebook Ads',
      icon: '📘',
      source: 'facebook',
      medium: 'cpc',
      campaign: 'fb_campaign_2026'
    },
    {
      name: 'Google Ads',
      icon: '🔍',
      source: 'google',
      medium: 'cpc',
      campaign: 'google_search_2026'
    },
    {
      name: 'Email Campaign',
      icon: '📧',
      source: 'newsletter',
      medium: 'email',
      campaign: 'email_campaign_2026'
    },
    {
      name: 'Instagram',
      icon: '📸',
      source: 'instagram',
      medium: 'social',
      campaign: 'ig_campaign_2026'
    },
    {
      name: 'LinkedIn',
      icon: '💼',
      source: 'linkedin',
      medium: 'social',
      campaign: 'linkedin_2026'
    },
    {
      name: 'Twitter/X',
      icon: '🐦',
      source: 'twitter',
      medium: 'social',
      campaign: 'twitter_2026'
    }
  ];

  // Common sources and mediums
  const commonSources = ['google', 'facebook', 'instagram', 'linkedin', 'twitter', 'newsletter', 'youtube', 'tiktok'];
  const commonMediums = ['cpc', 'social', 'email', 'organic', 'referral', 'display', 'affiliate', 'video'];

  const applyTemplate = (template) => {
    setSource(template.source);
    setMedium(template.medium);
    setCampaign(template.campaign);
  };

  const validateUrl = (url) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const generateUTM = () => {
    if (!websiteUrl || !source || !medium || !campaign) {
      alert('Please fill in all required fields (URL, Source, Medium, Campaign)');
      return;
    }

    if (!validateUrl(websiteUrl)) {
      alert('Please enter a valid website URL');
      return;
    }

    let url = websiteUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    const params = new URLSearchParams();
    params.append('utm_source', source.toLowerCase().replace(/\s+/g, '_'));
    params.append('utm_medium', medium.toLowerCase().replace(/\s+/g, '_'));
    params.append('utm_campaign', campaign.toLowerCase().replace(/\s+/g, '_'));
    if (term) params.append('utm_term', term.toLowerCase().replace(/\s+/g, '+'));
    if (content) params.append('utm_content', content.toLowerCase().replace(/\s+/g, '_'));

    const finalUrl = `${url}?${params.toString()}`;
    setGeneratedUrl(finalUrl);

    // Add to history
    const historyItem = {
      url: finalUrl,
      source,
      medium,
      campaign,
      timestamp: new Date().toLocaleString()
    };
    setUrlHistory([historyItem, ...urlHistory.slice(0, 4)]); // Keep last 5
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      alert('Failed to copy URL');
    }
  };

  const resetForm = () => {
    setWebsiteUrl('');
    setSource('');
    setMedium('');
    setCampaign('');
    setTerm('');
    setContent('');
    setGeneratedUrl('');
  };

  const shortenUrl = (url) => {
    if (url.length > 50) {
      return url.substring(0, 47) + '...';
    }
    return url;
  };

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl mb-6 shadow-2xl shadow-[#00809D]/50">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4">
            Advanced UTM Link Builder
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Create trackable campaign URLs with UTM parameters for accurate analytics and campaign tracking
          </p>
        </div>

        {/* Quick Templates */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-4 text-base sm:text-lg">Quick Templates</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {templates.map((template, index) => (
              <button
                key={index}
                onClick={() => applyTemplate(template)}
                className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-xl p-4 hover:border-[#00809D]/50 transition-all group text-center"
              >
                <div className="text-3xl mb-2">{template.icon}</div>
                <p className="text-white text-xs sm:text-sm font-medium group-hover:text-[#00809D] transition-colors">
                  {template.name}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input Form */}
          <div className="lg:col-span-2 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-[#00809D] to-[#00a0c4] rounded-full"></span>
              Build Your UTM URL
            </h2>

            <div className="space-y-5">
              {/* Website URL */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Website URL
                  <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <svg className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <input
                    type="text"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://yourwebsite.com/landing-page"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-12 pr-4 py-3 sm:py-4 text-white text-sm sm:text-base placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">The full URL of your landing page</p>
              </div>

              {/* Campaign Source */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Campaign Source
                  <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="google, facebook, newsletter"
                  list="sources"
                  className="w-full bg-black/50 border border-white/20 rounded-xl px-4 py-3 sm:py-4 text-white text-sm sm:text-base placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                />
                <datalist id="sources">
                  {commonSources.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Referrer: google, facebook, newsletter</p>
              </div>

              {/* Campaign Medium */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Campaign Medium
                  <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={medium}
                  onChange={(e) => setMedium(e.target.value)}
                  placeholder="cpc, email, social"
                  list="mediums"
                  className="w-full bg-black/50 border border-white/20 rounded-xl px-4 py-3 sm:py-4 text-white text-sm sm:text-base placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                />
                <datalist id="mediums">
                  {commonMediums.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Marketing medium: cpc, email, social</p>
              </div>

              {/* Campaign Name */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Campaign Name
                  <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={campaign}
                  onChange={(e) => setCampaign(e.target.value)}
                  placeholder="summer_sale_2026"
                  className="w-full bg-black/50 border border-white/20 rounded-xl px-4 py-3 sm:py-4 text-white text-sm sm:text-base placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                />
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Product, promo code, or slogan</p>
              </div>

              {/* Campaign Term (Optional) */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Campaign Term
                  <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="running+shoes"
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white text-sm sm:text-base placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                />
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Identify paid keywords</p>
              </div>

              {/* Campaign Content (Optional) */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Campaign Content
                  <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="logolink, textlink, banner_ad"
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white text-sm sm:text-base placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                />
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Differentiate similar content or links</p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                onClick={generateUTM}
                className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 sm:py-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 text-sm sm:text-base flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate UTM URL
              </button>
              <button
                onClick={resetForm}
                className="sm:w-auto bg-gray-800 text-white px-6 py-3 sm:py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all text-sm sm:text-base"
              >
                Reset All
              </button>
            </div>

            {/* Generated URL */}
            {generatedUrl && (
              <div className="mt-8 space-y-4 animate-in fade-in duration-500">
                <div className="bg-gradient-to-br from-[#00809D]/20 to-[#00a0c4]/20 border border-[#00809D]/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-white font-semibold text-sm sm:text-base">Your Trackable URL:</label>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-semibold">
                      ✓ Generated
                    </span>
                  </div>
                  <div className="bg-black/50 border border-white/20 rounded-xl p-4 mb-4 break-all text-gray-300 text-xs sm:text-sm max-h-32 overflow-y-auto">
                    {generatedUrl}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={copyToClipboard}
                      className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {showCopied ? 'Copied!' : 'Copy URL'}
                    </button>
                    <a
                      href={generatedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sm:w-auto bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-700 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Test URL
                    </a>
                  </div>
                </div>

                {/* URL Breakdown */}
                <div className="bg-gray-900/50 border border-white/10 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3 text-sm sm:text-base">Parameter Breakdown</h4>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <span className="text-[#00809D] font-mono">utm_source:</span>
                      <span className="text-white">{source}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <span className="text-[#00809D] font-mono">utm_medium:</span>
                      <span className="text-white">{medium}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <span className="text-[#00809D] font-mono">utm_campaign:</span>
                      <span className="text-white">{campaign}</span>
                    </div>
                    {term && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <span className="text-[#00809D] font-mono">utm_term:</span>
                        <span className="text-white">{term}</span>
                      </div>
                    )}
                    {content && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <span className="text-[#00809D] font-mono">utm_content:</span>
                        <span className="text-white">{content}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Info & History */}
          <div className="space-y-6">
            {/* UTM Guide */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                UTM Parameters
              </h3>
              <div className="space-y-4">
                <div className="bg-black/30 border border-white/10 rounded-xl p-4">
                  <p className="text-[#00809D] font-bold mb-1 text-sm">utm_source</p>
                  <p className="text-gray-400 text-xs">Identify traffic source</p>
                  <p className="text-gray-500 text-xs mt-1 font-mono">google, facebook, newsletter</p>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-xl p-4">
                  <p className="text-[#00809D] font-bold mb-1 text-sm">utm_medium</p>
                  <p className="text-gray-400 text-xs">Marketing medium type</p>
                  <p className="text-gray-500 text-xs mt-1 font-mono">cpc, email, social, organic</p>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-xl p-4">
                  <p className="text-[#00809D] font-bold mb-1 text-sm">utm_campaign</p>
                  <p className="text-gray-400 text-xs">Specific campaign name</p>
                  <p className="text-gray-500 text-xs mt-1 font-mono">summer_sale, product_launch</p>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-xl p-4">
                  <p className="text-[#00809D] font-bold mb-1 text-sm">utm_term</p>
                  <p className="text-gray-400 text-xs">Paid search keywords</p>
                  <p className="text-gray-500 text-xs mt-1 font-mono">running+shoes, nike</p>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-xl p-4">
                  <p className="text-[#00809D] font-bold mb-1 text-sm">utm_content</p>
                  <p className="text-gray-400 text-xs">Differentiate ads/links</p>
                  <p className="text-gray-500 text-xs mt-1 font-mono">logolink, textlink, banner</p>
                </div>
              </div>
            </div>

            {/* Recent URLs History */}
            {urlHistory.length > 0 && (
              <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Recent URLs
                </h3>
                <div className="space-y-3">
                  {urlHistory.map((item, index) => (
                    <div key={index} className="bg-black/30 border border-white/10 rounded-xl p-4 hover:border-[#00809D]/50 transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-white text-xs font-semibold mb-1 truncate">{item.campaign}</p>
                          <p className="text-gray-500 text-xs">{item.source} / {item.medium}</p>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(item.url);
                            setShowCopied(true);
                            setTimeout(() => setShowCopied(false), 2000);
                          }}
                          className="text-[#00809D] hover:text-[#00a0c4] transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-gray-600 text-xs">{item.timestamp}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Best Practices */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Best Practices
              </h3>
              <ul className="space-y-3 text-xs sm:text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-[#00809D] mt-0.5">•</span>
                  <span>Use lowercase and underscores instead of spaces</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-[#00809D] mt-0.5">•</span>
                  <span>Keep parameter values consistent across campaigns</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-[#00809D] mt-0.5">•</span>
                  <span>Use descriptive names that make sense in reports</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-[#00809D] mt-0.5">•</span>
                  <span>Document your UTM strategy for team consistency</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-[#00809D] mt-0.5">•</span>
                  <span>Test your URLs before launching campaigns</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4">Why Use UTM Parameters?</h3>
          <p className="text-gray-400 mb-8 leading-relaxed text-sm sm:text-base">
            UTM (Urchin Tracking Module) parameters are essential tags that help you track the effectiveness of your marketing campaigns 
            in Google Analytics, Facebook Pixel, and other analytics platforms. They provide detailed insights into which campaigns, 
            sources, and mediums drive the most traffic and conversions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-black/30 border border-white/10 rounded-2xl p-6 text-center hover:border-[#00809D]/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#00809D]/30">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Track Performance</h4>
              <p className="text-gray-400 text-xs sm:text-sm">Measure which campaigns drive the most traffic and conversions</p>
            </div>
            <div className="bg-black/30 border border-white/10 rounded-2xl p-6 text-center hover:border-[#00809D]/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#00809D]/30">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Optimize Budget</h4>
              <p className="text-gray-400 text-xs sm:text-sm">Identify which channels deliver the best ROI for smarter spending</p>
            </div>
            <div className="bg-black/30 border border-white/10 rounded-2xl p-6 text-center hover:border-[#00809D]/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#00809D]/30">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Data-Driven Decisions</h4>
              <p className="text-gray-400 text-xs sm:text-sm">Make informed marketing decisions based on accurate tracking data</p>
            </div>
          </div>
        </div>

        {/* Copy Toast */}
        {showCopied && (
          <div className="fixed bottom-8 right-8 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 rounded-xl shadow-2xl shadow-[#00809D]/50 animate-in fade-in slide-in-from-bottom-4 duration-300 flex items-center gap-2 z-50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">URL Copied to Clipboard!</span>
          </div>
        )}
      </div>
    </div>
  );
}
